/**
 * Resolve a customer's Chatwoot conversation (by LINE user id) so the dashboard
 * can deep-link into the embedded inbox.
 *
 * Calls the Control Plane whitelisted method get_customer_conversation. Mirrors
 * fetchChatwootSso's auth quirk: JWT goes in the BODY (`access_token`), never an
 * Authorization header (Frappe rejects unknown Bearer tokens on allow_guest
 * endpoints). The account is derived server-side from the JWT tenant.
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

export interface CustomerConversationResult {
  success: boolean;
  account_id?: number;
  conversation_id?: number | null;
  found?: boolean;
  reason?: string;
}

export interface CustomerChatMessage {
  content: string;
  at: string | null;
  incoming: boolean;
}

export interface CustomerChatSummary {
  success: boolean;
  found?: boolean;
  account_id?: number;
  conversation_id?: number | null;
  contact_name?: string | null;
  messages?: CustomerChatMessage[];
  reason?: string;
}

export async function fetchCustomerChatSummary(args: {
  lineUserId?: string;
  ownerEmail?: string;
  limit?: number;
}): Promise<CustomerChatSummary> {
  const token = getCPToken();
  if (!token) throw new Error("Not authenticated to Control Plane");
  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.chatwoot_customer.get_customer_chat_summary`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        line_user_id: args.lineUserId,
        owner_email: args.ownerEmail,
        limit: args.limit ?? 5,
      }),
    },
  );
  if (!response.ok) throw new Error(`Control Plane responded ${response.status}`);
  const data = await response.json();
  return (data?.message ?? data) as CustomerChatSummary;
}

export async function fetchCustomerConversation(args: {
  lineUserId?: string;
  ownerEmail?: string;
}): Promise<CustomerConversationResult> {
  const token = getCPToken();
  if (!token) throw new Error("Not authenticated to Control Plane");

  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.chatwoot_customer.get_customer_conversation`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        line_user_id: args.lineUserId,
        owner_email: args.ownerEmail,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Control Plane responded ${response.status}`);
  }

  const data = await response.json();
  // Frappe wraps whitelisted return values in `.message`.
  return (data?.message ?? data) as CustomerConversationResult;
}
